import clsx from 'clsx';
import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { LS_KEY_ENV_INDICATOR_PINNED } from 'consts';
import icons, { PushPinIcon, PushPinOutlinedIcon } from 'icons';
import { Chip, styled } from 'netline-ui';
import services from 'services';
import { EnvMode } from 'types';
import { getMainConfig, resolveEnvConfig } from 'utils';

import styles, {
  environmentIndicatorChipClasses,
  environmentIndicatorClasses,
  environmentIndicatorRootClasses,
} from './EnvironmentIndicator.styles';

const envIconMap: Record<EnvMode, string | null> = {
  [EnvMode.Dev]: 'CodeIcon',
  [EnvMode.Test]: 'PestControlIcon',
  [EnvMode.Int]: 'ExtensionIcon',
  [EnvMode.Prod]: 'PeopleIcon',
  [EnvMode.Custom]: null,
};
const EnvironmentIndicatorRoot = styled('div', { name: environmentIndicatorClasses.$name })(styles);

const MOUSE_ENTER_TIMEOUT = 500; // ms

const EnvironmentIndicator = () => {
  const envConfig = getMainConfig('env');
  const { title, mode, version, allowEnvIndicator, customIcon } = useMemo(
    () => resolveEnvConfig(envConfig),
    [envConfig],
  );
  const icon = (mode && envIconMap[mode]) || envIconMap.custom;
  const iconName = icon ?? customIcon;
  const Icon = iconName ? icons[iconName] : null;

  const [isIndicatorPinned, setIndicatorPinned] = useState(
    services.appFrame.userStorage.getItem(LS_KEY_ENV_INDICATOR_PINNED) ?? true,
  );
  const [showIndicator, setShowIndicator] = useState(isIndicatorPinned);

  const handleIndicatorPin = useCallback(() => {
    services.appFrame.userStorage.setItem(LS_KEY_ENV_INDICATOR_PINNED, !isIndicatorPinned);
    setIndicatorPinned(!isIndicatorPinned);
  }, [isIndicatorPinned]);

  const PinIcon = isIndicatorPinned ? PushPinIcon : PushPinOutlinedIcon;

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const handleMouseEnter = useCallback(() => {
    timerRef.current = setTimeout(() => {
      setShowIndicator(true);
    }, MOUSE_ENTER_TIMEOUT);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setShowIndicator(false);
  }, []);

  return (
    allowEnvIndicator && (
      <EnvironmentIndicatorRoot
        className={clsx(
          environmentIndicatorClasses.root,
          (mode && environmentIndicatorRootClasses[mode]) ?? environmentIndicatorRootClasses.custom,
        )}
        data-testid="EnvironmentIndicator-root"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className={environmentIndicatorClasses.stripe} />
        <Chip
          label={version ? `${title} ${version}` : title}
          icon={Icon && <Icon data-testid="EnvironmentIndicator-icon" />}
          className={clsx(environmentIndicatorClasses.chip, {
            [environmentIndicatorChipClasses.hidden]: !isIndicatorPinned && !showIndicator,
          })}
          data-testid="EnvironmentIndicator-chip"
          classes={{
            label: environmentIndicatorClasses.chipLabel,
            icon: environmentIndicatorClasses.chipIcon,
            deleteIcon: environmentIndicatorClasses.chipPinIcon,
          }}
          deleteIcon={<PinIcon data-testid="EnvironmentIndicator-pinIcon" />}
          onDelete={handleIndicatorPin}
        />
      </EnvironmentIndicatorRoot>
    )
  );
};

export default memo(EnvironmentIndicator);
